import {MessageService} from 'src/app/services/message.service';
import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, FormArray, Validators, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Weekday} from '@capacitor/local-notifications';
import {OsNotificationService, Reminder} from '../../services/os-notification.service';

interface DayOfWeek {
  id: Weekday;
  name: string;
  short: string;
}

@Component({
  standalone: true,
  selector: 'app-notification-settings',
  templateUrl: './notification-settings.component.html',
  styleUrls: ['./notification-settings.component.scss'],
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationSettingsComponent implements OnInit {
  settingsForm: FormGroup;
  nextId = 1;

  daysOfWeek: DayOfWeek[] = [
    {id: Weekday.Monday, name: 'Monday', short: 'M'},
    {id: Weekday.Tuesday, name: 'Tuesday', short: 'T'},
    {id: Weekday.Wednesday, name: 'Wednesday', short: 'W'},
    {id: Weekday.Thursday, name: 'Thursday', short: 'T'},
    {id: Weekday.Friday, name: 'Friday', short: 'F'},
    {id: Weekday.Saturday, name: 'Saturday', short: 'S'},
    {id: Weekday.Sunday, name: 'Sunday', short: 'S'},
  ];

  constructor(
    private fb: FormBuilder,
    private osNotificationService: OsNotificationService,
    private messageService: MessageService
  ) {
    this.settingsForm = this.fb.group({
      reminders: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.loadSettings();
  }

  get reminders(): FormArray {
    return this.settingsForm.get('reminders') as FormArray;
  }

  private createReminderForm(reminder?: Reminder): FormGroup {
    const defaultDays: Weekday[] = reminder?.days || [
      Weekday.Monday,
      Weekday.Tuesday,
      Weekday.Wednesday,
      Weekday.Thursday,
      Weekday.Friday,
    ];
    return this.fb.group({
      id: [reminder?.id || this.nextId++],
      time: [reminder?.time || '09:00', Validators.required],
      enabled: [reminder?.enabled ?? true],
      label: [reminder?.label || 'Daily Reminder'],
      days: [defaultDays, Validators.required],
    });
  }

  private loadSettings(): void {
    const savedReminders = localStorage.getItem('notificationReminders');

    if (savedReminders) {
      let reminders: Reminder[] = JSON.parse(savedReminders);

      // Migrate old day format (0-6) to Capacitor Weekday enum (1-7)
      reminders = reminders.map(reminder => ({
        ...reminder,
        days: reminder.days.map(day => {
          const dayNum = day as number;
          // If using old format (0-6), convert to Capacitor weekday
          if (dayNum < 1 || dayNum > 7) {
            // 0=Monday -> 2, 1=Tuesday -> 3, ..., 6=Sunday -> 1
            return (dayNum === 6 ? 1 : dayNum + 2) as Weekday;
          }
          // Already in new format (1-7)
          return dayNum as Weekday;
        }),
      }));

      // Deduplicate and validate days array on load
      reminders = reminders.map(reminder => ({
        ...reminder,
        days: Array.from(new Set(reminder.days))
          .filter((day): day is Weekday => {
            const numDay = day as number;
            return numDay >= 1 && numDay <= 7;
          })
          .sort((a, b) => (a as number) - (b as number)),
      }));

      this.nextId = Math.max(...reminders.map(r => r.id), 0) + 1;

      reminders.forEach(reminder => {
        this.reminders.push(this.createReminderForm(reminder));
      });
    } else {
      // Migrate from old single notification format
      const savedTime = localStorage.getItem('notificationTime');
      if (savedTime) {
        this.addReminder(savedTime, 'Daily Reminder');
        localStorage.removeItem('notificationTime');
      } else {
        // Add default reminder
        this.addReminder('09:00', 'Morning Reminder');
      }
    }
  }

  addReminder(time = '09:00', label = 'New Reminder'): void {
    const reminder: Reminder = {
      id: this.nextId++,
      time,
      enabled: true,
      label,
      days: [Weekday.Monday, Weekday.Tuesday, Weekday.Wednesday, Weekday.Thursday, Weekday.Friday],
    };
    this.reminders.push(this.createReminderForm(reminder));
  }

  removeReminder(index: number): void {
    this.reminders.removeAt(index);
  }

  isDaySelected(reminderIndex: number, dayId: Weekday): boolean {
    const reminderForm = this.reminders.at(reminderIndex);
    const selectedDays: Weekday[] = reminderForm.get('days')?.value || [];
    return selectedDays.includes(dayId);
  }

  toggleDay(reminderIndex: number, dayId: Weekday): void {
    const reminderForm = this.reminders.at(reminderIndex);
    const selectedDays: Weekday[] = reminderForm.get('days')?.value || [];

    if (selectedDays.includes(dayId)) {
      const newDays = selectedDays.filter(d => d !== dayId);
      if (newDays.length > 0) {
        reminderForm.get('days')?.setValue(newDays);
      }
    } else {
      // Use Set to prevent duplicates
      const uniqueDays = Array.from(new Set([...selectedDays, dayId]));
      reminderForm.get('days')?.setValue(uniqueDays.sort((a, b) => (a as number) - (b as number)));
    }
  }

  async onSaveSettings(): Promise<void> {
    if (this.settingsForm.valid) {
      // Ensure notification permissions are granted
      const permissionGranted = await this.osNotificationService.ensureNotificationPermissions();

      if (!permissionGranted) {
        // Show error if permissions not granted
        this.messageService.add('Notification permissions are required to schedule reminders.', 'error');
        return;
      }

      let reminders: Reminder[] = this.reminders.value;

      // Deduplicate and validate days array
      reminders = reminders.map(reminder => ({
        ...reminder,
        days: Array.from(new Set(reminder.days))
          .filter((day): day is Weekday => {
            const numDay = day as number;
            return numDay >= 1 && numDay <= 7;
          })
          .sort((a, b) => (a as number) - (b as number)),
      }));

      console.log('Saving reminders to localStorage:', JSON.stringify(reminders));
      localStorage.setItem('notificationReminders', JSON.stringify(reminders));

      console.log('Calling scheduleMultipleNotifications with:', JSON.stringify(reminders));
      await this.osNotificationService.scheduleMultipleNotifications(reminders);

      // Show success
      this.messageService.add('Reminders scheduled successfully!', 'taskCompleted');
    }
  }

  async onSendTestNotification(): Promise<void> {
    await this.osNotificationService.sendTestNotification();

    // Show test notification
    this.messageService.add('Test notification scheduled for 2 seconds', 'taskCompleted');
  }
}
